import { ShrimpHealthThreshold } from 'modules/notifications/api/types'
import {
  useMutateCreateNewMedicineRow,
  useMutateRemoveMedicineRow,
} from 'modules/pond/api/mutation'
import { MedicineCategory } from 'modules/pond/api/types'
import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import palette from 'theme/palette'
import shadows from 'theme/shadows'

import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

import LoadingIconButton from 'components/LoadingIconButton'

import {
  LABEL_TRANSLATION,
  CATEGORIES_ALLOW_REMOVE,
  TableIDMapCategory,
  LabelGroup,
  GROUP_DIVIDER_BORDER,
} from '../constants'
import { tableStyles } from '../styles'
import {
  getLabelBackgroundColor,
  hasLabelRowSpan,
  hasLabelTypeText,
  isGroupDividerBottomRow,
} from '../utils'
import { BootstrapData, EditableCell } from './EditableCell'

interface RowData {
  group: string
  label: string
  values: (number | string)[]
  editable: boolean
  id: number[]
  showGroup: boolean
  groupSpan: number
  isGroupMiddle: boolean
  isLastInGroup: boolean
  isBeforeDividerGroup?: boolean
}

interface VirtualRowProps {
  row: RowData
  rowHeight: number
  translateY: number
  onCellChange: (params: {
    group: string
    label: string
    id: number
    columnIndex: number
    value: string
  }) => Promise<void>
  editedCells?: Map<string, string>
  bootstrap?: BootstrapData
  // When true the row renders in normal flow (not absolutely positioned) so it
  // can live inside the frozen sticky header block — used to pin the อายุกุ้ง
  // row below the date header (NK-206).
  pinned?: boolean
  // Shrimp age (days) per column, read off the อายุกุ้ง row (NK-272).
  shrimpAges?: (number | undefined)[]
  // Shrimp-health alert settings, for the vibrio cell colours (NK-273).
  shrimpHealthThresholds?: ShrimpHealthThreshold[]
}

export const VirtualRow = memo(
  ({
    row,
    rowHeight,
    translateY,
    onCellChange,
    editedCells,
    bootstrap,
    pinned = false,
    shrimpAges,
    shrimpHealthThresholds,
  }: VirtualRowProps) => {
    const { t } = useTranslation('pondOverview')
    const createNewRow = useMutateCreateNewMedicineRow()
    const removeRow = useMutateRemoveMedicineRow()

    const showAddRemoveButtons = useMemo(() => {
      const isAllowGroup = CATEGORIES_ALLOW_REMOVE.includes(row.group as any)
      const hasAtLeast3Labels = row.values.length >= 3
      return isAllowGroup && hasAtLeast3Labels && row.isLastInGroup
    }, [row.group, row.values.length, row.isLastInGroup])

    const showRemoveButton = useMemo(() => {
      const labelIsThree = row.label === '3' || Number(row.label) === 3
      return !labelIsThree && row.values.length > 3
    }, [row.label, row.values.length])

    const handleCellChangeByColumn = useMemo(() => {
      return row.values.map(
        (_, colIdx) => (newVal: string) =>
          onCellChange({
            group: row.group,
            label: row.label,
            id: row.id[colIdx],
            columnIndex: colIdx,
            value: newVal,
          })
      )
    }, [onCellChange, row.group, row.label, row.id])

    const cellsData = useMemo(() => {
      return row.values.map((val, colIdx) => {
        const isCellEditable = row.editable
        const cellId = row.id[colIdx]
        const cellKey = `${row.group}_${row.label}_${cellId}`
        const editedValue = editedCells?.get(cellKey)
        const displayValue = editedValue !== undefined ? editedValue : val ?? '-'

        return {
          displayValue,
          isCellEditable,
          cellId,
          colIdx,
        }
      })
    }, [row.values, row.id, row.group, row.label, row.editable, editedCells])

    const translateLabel = (label: string): string => {
      if (label in LABEL_TRANSLATION) {
        return t(LABEL_TRANSLATION[label as keyof typeof LABEL_TRANSLATION])
      }
      return label
    }

    const isMobile = new URLSearchParams(window.location.search).get('isMobile') === 'true'
    const rowZIndex = row.showGroup ? 5 : 10

    // Section-opening / -closing rows: draw the emphasized divider across every
    // cell of the row (both sticky label columns and each data cell) so each
    // edge of the ผลกุ้ง + ผลน้ำ block reads as one unbroken line.
    const showGroupDivider =
      isGroupDividerBottomRow(row.group, row.isLastInGroup) || !!row.isBeforeDividerGroup

    return (
      <div
        style={{
          ...tableStyles.row,
          position: pinned ? 'relative' : 'absolute',
          top: pinned ? undefined : 0,
          left: 0,
          zIndex: rowZIndex,
          width: '100%',
          height: `${rowHeight}px`,
          // Opaque so scrolling data rows don't show through the pinned row's
          // otherwise-transparent data cells.
          ...(pinned ? { backgroundColor: palette.light.background.default } : {}),
          transform: pinned ? undefined : `translateY(${translateY}px)`,
        }}
      >
        {hasLabelRowSpan(row.label) ? (
          <div
            style={{
              ...tableStyles.stickyCol1,
              color: row.group === LabelGroup.ShrimpAge ? palette.light.grey[600] : undefined,
              overflow: 'visible',
              ...(row.isGroupMiddle ? tableStyles.groupCell : {}),
              ...(hasLabelTypeText(row.group) ? tableStyles.textType : {}),
              borderLeft: `1.5px solid ${palette.light.grey[300]}`,
              borderRight: `4px solid ${palette.light.grey[300]}`,
              borderBottom: showGroupDivider
                ? GROUP_DIVIDER_BORDER
                : row.isLastInGroup
                ? `1px solid ${palette.light.grey[300]}`
                : 'none',
              // These label cells are content-box while the data cells are
              // border-box; without this the thick rule would render 4px lower
              // here than in the data columns and the line would look stepped.
              boxSizing: showGroupDivider ? ('border-box' as const) : undefined,
              fontSize: isMobile ? '1.625rem' : undefined,
              fontWeight: isMobile ? 700 : undefined,
            }}
          >
            {row.isGroupMiddle ? translateLabel(row.group) : ''}
          </div>
        ) : (
          <>
            <div
              style={{
                position: 'sticky' as const,
                left: '0px',
                zIndex: 5,
                width: '120px',
                ...tableStyles.groupCell,
                ...(hasLabelTypeText(row.label) ? tableStyles.textType : {}),
                borderLeft: `1.5px solid ${palette.light.grey[300]}`,
                borderBottom: showGroupDivider
                  ? GROUP_DIVIDER_BORDER
                  : row.isLastInGroup
                  ? `1.5px solid ${palette.light.grey[300]}`
                  : 'none',
                boxSizing: showGroupDivider ? ('border-box' as const) : undefined,
                fontSize: isMobile ? '1.625rem' : undefined,
                fontWeight: isMobile ? 700 : undefined,
              }}
            >
              {row.isGroupMiddle ? translateLabel(row.group) : ''}
            </div>

            <div
              style={{
                position: 'sticky' as const,
                left: '120px',
                zIndex: 5,
                width: '180px',
                ...tableStyles.groupCell,
                ...(hasLabelTypeText(row.label) ? tableStyles.textType : {}),
                backgroundColor: getLabelBackgroundColor(row.label) || palette.light.grey[200],
                borderRight: `4px solid ${palette.light.grey[300]}`,
                borderBottom: showGroupDivider
                  ? GROUP_DIVIDER_BORDER
                  : `1.5px solid ${palette.light.grey[300]}`,
                boxSizing: showGroupDivider ? ('border-box' as const) : undefined,
                overflow: 'visible',
                fontSize: isMobile ? '1.625rem' : undefined,
                fontWeight: isMobile ? 700 : undefined,
              }}
            >
              {translateLabel(row.label)}
              {showAddRemoveButtons && (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      left: -10,
                      top: -10,
                      zIndex: 10,
                    }}
                  >
                    {showRemoveButton && (
                      <LoadingIconButton
                        loading={removeRow.isLoading}
                        onClick={() => {
                          if (!showRemoveButton) return
                          removeRow.mutate(
                            TableIDMapCategory[
                              row.group as keyof typeof TableIDMapCategory
                            ] as MedicineCategory
                          )
                        }}
                        style={{
                          background: palette.light.error.main,
                          color: palette.light.background.default,
                          boxShadow: shadows.light[10],
                          width: 20,
                          height: 20,
                        }}
                      >
                        <RemoveIcon color="inherit" />
                      </LoadingIconButton>
                    )}
                  </div>

                  <div
                    style={{
                      position: 'absolute',
                      right: -10,
                      bottom: -15,
                      zIndex: 10,
                    }}
                  >
                    <LoadingIconButton
                      loading={createNewRow.isLoading}
                      onClick={() =>
                        createNewRow.mutate(
                          TableIDMapCategory[
                            row.group as keyof typeof TableIDMapCategory
                          ] as MedicineCategory
                        )
                      }
                      style={{
                        background: palette.light.primary.main,
                        color: palette.light.background.default,
                        boxShadow: shadows.light[10],
                        width: 20,
                        height: 20,
                      }}
                    >
                      <AddIcon color="inherit" />
                    </LoadingIconButton>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Editable cells */}
        {cellsData.map((cell) => (
          <div
            key={cell.colIdx}
            style={{
              ...tableStyles.cell,
              ...(hasLabelTypeText(row.label) ? tableStyles.textType : {}),
            }}
          >
            <EditableCell
              value={cell.displayValue}
              onChange={handleCellChangeByColumn[cell.colIdx]}
              editable={cell.isCellEditable}
              label={row.label}
              group={row.group}
              id={cell.cellId}
              columnIndex={cell.colIdx}
              bootstrap={bootstrap}
              hasGroupDivider={showGroupDivider}
              shrimpAge={shrimpAges?.[cell.colIdx]}
              shrimpHealthThresholds={shrimpHealthThresholds}
            />
          </div>
        ))}
      </div>
    )
  },
  (prev, next) => {
    if (prev.translateY !== next.translateY) return false
    if (prev.rowHeight !== next.rowHeight) return false

    if (prev.row.group !== next.row.group) return false
    if (prev.row.label !== next.row.label) return false

    if (prev.row.values.length !== next.row.values.length) return false
    if (prev.row.id.length !== next.row.id.length) return false

    for (let i = 0; i < prev.row.values.length; i++) {
      if (prev.row.values[i] !== next.row.values[i]) return false
    }
    for (let i = 0; i < prev.row.id.length; i++) {
      if (prev.row.id[i] !== next.row.id[i]) return false
    }

    if (prev.row.showGroup !== next.row.showGroup) return false
    if (prev.row.groupSpan !== next.row.groupSpan) return false
    if (prev.row.isGroupMiddle !== next.row.isGroupMiddle) return false
    if (prev.row.isLastInGroup !== next.row.isLastInGroup) return false

    if (prev.row.editable !== next.row.editable) return false

    if (prev.editedCells !== next.editedCells) return false

    if (prev.bootstrap !== next.bootstrap) return false

    if (prev.pinned !== next.pinned) return false

    if (prev.shrimpAges !== next.shrimpAges) return false

    if (prev.shrimpHealthThresholds !== next.shrimpHealthThresholds) return false

    return true
  }
)

VirtualRow.displayName = 'VirtualRow'
